import { FC } from "react"
import Input from "../../form/input"
import Select from "../../form/select"
import Checkbox from "../../form/checkbox"
import { ARM_TERM_OPTIONS, NONQM_PROGRAM_OPTIONS } from "../../../constants"

const PROFILE_TYPE_OPTIONS = [
  { id: "conventional", name: "Conventional" },
  { id: "fha", name: "FHA" },
  { id: "va", name: "VA" },
  { id: "usda", name: "USDA" },
  { id: "nonconforming", name: "Non-Conforming" },
  { id: "nonqm", name: "Non-QM" }
] as const

const AMORTIZATION_OPTIONS = [
  { id: "Fixed", name: "Fixed" },
  { id: "ARM", name: "ARM" }
] as const

const LOAN_PURPOSE_OPTIONS = [
  { id: "", name: "None" },
  { id: "purchase", name: "Purchase" },
  { id: "simple_refinance", name: "Simple Refinance" },
  { id: "refinance_cash_out", name: "Refinance Cash Out" }
] as const

interface Props {
  values: Record<string, any>
  setFormField: (name: string, val: string | number | boolean) => void
}

const ManageProfileFormContent: FC<Props> = ({ values, setFormField }) => {
  return (
    <div className="py-6 space-y-8">
      {/* Core Details */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
          Profile Details
        </h3>
        <div className="space-y-6">
          <Input
            label="Title"
            name="title"
            placeholder="e.g. FHA Home Loan 640+"
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Loan Type"
              name="type"
              data={PROFILE_TYPE_OPTIONS}
              selected={values.type ?? ""}
              setSelected={(val: string) => setFormField("type", val)}
              required
            />
            <Select
              label="Amortization"
              name="amortization_type"
              data={AMORTIZATION_OPTIONS}
              selected={values.amortization_type ?? ""}
              setSelected={(val: string) =>
                setFormField("amortization_type", val)
              }
              required
            />
          </div>
          <Select
            label="Loan Purpose"
            name="loan_purpose"
            data={LOAN_PURPOSE_OPTIONS}
            selected={values.loan_purpose ?? ""}
            setSelected={(val: string) => setFormField("loan_purpose", val)}
          />
          {values.type === "nonqm" && (
            <Select
              label="Non-QM Program"
              name="nonqm_program"
              data={NONQM_PROGRAM_OPTIONS}
              selected={values.nonqm_program ?? ""}
              setSelected={(val: string) => setFormField("nonqm_program", val)}
              required
            />
          )}
        </div>
      </div>

      {/* Loan Parameters */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
          Loan Parameters
        </h3>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Down Payment %"
              name="dp_percent"
              type="number"
              placeholder="e.g. 3.5"
              required
              inputProps={{ step: "0.25", min: "0", max: "100" }}
            />
            <Input
              label="Min Credit Score"
              name="min_credit"
              type="number"
              placeholder="e.g. 640"
              required
              inputProps={{ min: "300", max: "850" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Term (months)"
              name="term"
              type="number"
              placeholder="e.g. 360"
              required
              inputProps={{ min: "1" }}
            />
            <Input
              label="Sample List Price"
              name="sample_list_price"
              type="number"
              placeholder="e.g. 350000"
              required
              inputProps={{ min: "0" }}
            />
          </div>
          {values.amortization_type === "ARM" && (
            <Select
              label="ARM Term"
              name="arm_term"
              data={ARM_TERM_OPTIONS}
              selected={values.arm_term ?? ""}
              setSelected={(val: string) => setFormField("arm_term", val)}
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Lock Period (days)"
              name="lock_period"
              type="number"
              placeholder="30"
              inputProps={{ min: "0" }}
            />
            <Input
              label="Fee"
              name="fee"
              type="number"
              placeholder="0"
              inputProps={{ min: "0", step: "0.01" }}
            />
          </div>
          <Input
            label="Max Loan Amount"
            name="max_loan_amt"
            type="number"
            placeholder="0 for no limit"
            inputProps={{ min: "0" }}
          />
        </div>
      </div>

      {/* Eligibility */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
          Eligibility
        </h3>
        <div className="space-y-4">
          <Checkbox
            label="Primary Residence"
            subLabel="This profile is for primary residence only"
            name="primary_residence"
          />
          <Checkbox
            label="First Time Homebuyer"
            subLabel="Restricted to first-time homebuyers"
            name="first_time_homebuyer"
          />
          <Checkbox
            label="VA First Time Use"
            subLabel="First-time use of VA benefit"
            name="va_first_time_use"
          />
        </div>
      </div>

      {/* Advanced */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
          Advanced
        </h3>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Cash Out"
              name="cash_out"
              type="number"
              placeholder="0"
              inputProps={{ min: "0" }}
            />
            <Input
              label="DTI"
              name="dti"
              type="number"
              placeholder="0"
              inputProps={{ min: "0", max: "100", step: "0.01" }}
            />
          </div>
          <Input label="Lead Source" name="lead_source" placeholder="" />
          <Input label="AUS" name="aus" placeholder="" />
          <Input label="Label" name="label" placeholder="Display label" />
        </div>
      </div>
    </div>
  )
}

export default ManageProfileFormContent
